import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ROUTES } from '@/shared/constants/routes';

type Language = 'fa' | 'en';

/**
 * Custom hook for language management with RTL support
 * Replaces the old LanguageContext
 */
export const useLanguage = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Get current language
  const language = (i18n.language as Language) || 'fa';
  const isRTL = language === 'fa';

  // Sync language with URL path (for home and pricing routes)
  useEffect(() => {
    const path = location.pathname;
    // Sync language for home and pricing routes
    const isHomeRoute = path === ROUTES.HOME || path === ROUTES.HOME_EN;
    const isPricingRoute = path === ROUTES.PRICING || path === ROUTES.PRICING_EN;
    
    if (isHomeRoute || isPricingRoute) {
      const urlLang = path.startsWith('/en') ? 'en' : 'fa';
      
      if (i18n.language !== urlLang) {
        i18n.changeLanguage(urlLang);
      }
    }
  }, [location.pathname, i18n]);

  // Update URL when language changes (for home and pricing routes)
  useEffect(() => {
    const path = location.pathname;
    const isHomeRoute = path === ROUTES.HOME || path === ROUTES.HOME_EN;
    const isPricingRoute = path === ROUTES.PRICING || path === ROUTES.PRICING_EN;
    
    if (isHomeRoute || isPricingRoute) {
      const urlLang = path.startsWith('/en') ? 'en' : 'fa';
      
      if (language !== urlLang) {
        if (isHomeRoute) {
          // Redirect home routes
          if (language === 'en') {
            navigate(ROUTES.HOME_EN);
          } else {
            navigate(ROUTES.HOME);
          }
        } else if (isPricingRoute) {
          // Redirect pricing routes
          if (language === 'en') {
            navigate(ROUTES.PRICING_EN);
          } else {
            navigate(ROUTES.PRICING);
          }
        }
      }
    }
  }, [language, navigate, location.pathname]);

  const toggleLanguage = () => {
    const newLanguage: Language = language === 'fa' ? 'en' : 'fa';
    i18n.changeLanguage(newLanguage);
    
    // Navigate to appropriate route
    if (newLanguage === 'en') {
      navigate(ROUTES.HOME_EN);
    } else {
      navigate(ROUTES.HOME);
    }
  };

  const setLanguage = (lang: Language) => {
    i18n.changeLanguage(lang);
    
    if (lang === 'en') {
      navigate(ROUTES.HOME_EN);
    } else {
      navigate(ROUTES.HOME);
    }
  };

  return {
    language,
    setLanguage,
    toggleLanguage,
    t, // translation function
    isRTL,
    // For backward compatibility - keep translations object
    translations: {
      seoTitle: t('seo.title'),
      metaDescription: t('seo.metaDescription'),
      h1: t('common.h1'),
      description: t('common.description'),
      toolName: t('common.toolName'),
      searchPlaceholder: t('search.placeholder'),
      searchButton: t('search.button'),
      language: t('form.language'),
      country: t('form.country'),
      keyword: t('form.keyword'),
      length: t('table.length'),
      parentKeyword: t('table.parentKeyword'),
      source: t('table.source'),
      searchVolume: t('table.searchVolume'),
      difficulty: t('table.difficulty'),
      intent: t('table.intent'),
      purpose: t('table.purpose'),
      loginSignup: t('common.loginSignup'),
      faqTitle: t('faq.title'),
      faqQ1: t('faq.q1'),
      faqA1: t('faq.a1'),
      faqQ2: t('faq.q2'),
      faqA2: t('faq.a2'),
      faqQ3: t('faq.q3'),
      faqA3: t('faq.a3'),
    },
  };
};

